import { useAuthentication } from "@/composables/useAuthentication";
import { isJsonLayerSymbolSummary, JsonLayerSymbolSummary } from "@/interfaces/chartListSummary";
import { isJsonLayerResponse, JsonLayerResponse } from "@/interfaces/jsonLayerResponse";

interface FetchChartListSummaryJsonLayerResponse extends JsonLayerResponse {
  symbols: any[];
}

function isFetchChartListSummaryJsonLayerResponse(jsonLayerResponse: JsonLayerResponse): jsonLayerResponse is FetchChartListSummaryJsonLayerResponse {
  return (
    'symbols' in jsonLayerResponse &&
    Array.isArray(jsonLayerResponse.symbols)
  );
}

export async function fetchChartListSummary(chartListListNum: string): Promise<JsonLayerSymbolSummary[]> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error(`Unable to fetch ChartList summary for chartListListNum: ${chartListListNum} - user is not logged in.`);
  }

  const chartCodeCopy = chartCode.value;
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'get-symbol-data',
        chartCode: chartCodeCopy,
        ln: chartListListNum,
        optionalFields: 'summaryview%2Csectorsymbol'
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info(`ChartList summary jsonLayerResponse for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum}.`, jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to fetch ChartList summary for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum}.`, e);

    throw new Error(`Failed to fetch ChartList summary for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to fetch ChartList summary for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to fetch ChartList summary for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  if (!isFetchChartListSummaryJsonLayerResponse(jsonLayerResponse)) { // TODO: Does not currently hard check each inner-object. Only checks if there is an array of objects within
    throw new Error(`Failed to fetch ChartList summary for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum} - jsonLayerResponse.success is true but jsonLayerResponse does not match FetchChartListSummaryJsonLayerResponse interface - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  const jsonLayerSymbolSummaries = jsonLayerResponse.symbols.filter(isJsonLayerSymbolSummary);
  const numInvalidJsonLayerSymbolSummaries = jsonLayerResponse.symbols.length - jsonLayerSymbolSummaries.length;

  if (numInvalidJsonLayerSymbolSummaries > 0) {
    window.sc3WorkbenchLogger.warn(`json-layer get-symbol-data command returned ${numInvalidJsonLayerSymbolSummaries} invalid Symbol Summaries (which did not match the JsonLayerSymbolSummary interface) for chartCode: ${chartCodeCopy} and chartListListNum: ${chartListListNum}.`);
  }

  return jsonLayerSymbolSummaries;
}
